import React from "react";
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Plus, X } from "lucide-react";
import { T, TYPE, labelSx } from "../user/complaints/shared";
import { usePm } from "./PmContext";
import { fieldSx } from "./inputs";

// Editors for field conditions (workflow transition rules, automation IF) and
// actions (workflow post functions, automation THEN). The server validates
// both again; these only make valid input easy to produce.

export const CONDITION_FIELDS = [
  ["type", "Issue type key (epic, story, task, bug…)"], ["status", "Status name"], ["priority", "Priority name"], ["priorityLevel", "Priority level (1–5)"],
  ["assigneeId", "Assignee id"], ["hasAssignee", "Has assignee (true/false)"], ["storyPoints", "Story points"], ["labels", "Labels"],
  ["statusCategory", "Status category"], ["hoursSinceCreated", "Hours since created"], ["hoursSinceUpdated", "Hours since updated"],
  ["daysInStatus", "Days in current status"], ["hoursInStatus", "Hours in current status"], ["flagged", "Flagged (true/false)"], ["changes.statusId.to", "Changed: new status id"],
  ["changes.statusCategory.to", "Changed: new status category"], ["changes.assignee.to", "Changed: new assignee"],
];
export const CONDITION_OPS = ["eq", "neq", "in", "nin", "contains", "not_contains", "gt", "gte", "lt", "lte", "exists", "not_exists"];

export const ConditionRows = ({ value = { all: [], any: [] }, onChange }) => {
  const ctx = usePm();
  const fields = [...CONDITION_FIELDS, ...ctx.customFields.map((f) => [`customFields.${f.key}`, `Custom: ${f.label}`])];
  const set = (g, list) => onChange({ ...value, [g]: list });
  return (
    <Box>
      {[["all", "All of these"], ["any", "Any of these"]].map(([g, title]) => (
        <Box key={g} sx={{ mb: 1 }}>
          <Typography sx={{ ...labelSx, fontSize: "0.66rem", mb: 0.5 }}>{title}</Typography>
          {(value[g] || []).map((c, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 0.75 }} alignItems="center">
              <TextField select size="small" value={c.field} onChange={(e) => set(g, value[g].map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))} sx={{ ...fieldSx, minWidth: 200 }}>
                {fields.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
              <TextField select size="small" value={c.op} onChange={(e) => set(g, value[g].map((x, j) => (j === i ? { ...x, op: e.target.value } : x)))} sx={{ ...fieldSx, width: 120 }}>
                {CONDITION_OPS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
              {!["exists", "not_exists"].includes(c.op) && (
                <TextField size="small" placeholder={["in", "nin"].includes(c.op) ? "a, b, c" : "value"} value={Array.isArray(c.value) ? c.value.join(", ") : c.value ?? ""}
                  onChange={(e) => set(g, value[g].map((x, j) => (j === i ? { ...x, value: ["in", "nin"].includes(x.op) ? e.target.value.split(",").map((s) => s.trim()).filter(Boolean) : e.target.value } : x)))} sx={{ ...fieldSx, flexGrow: 1 }} />
              )}
              <IconButton size="small" onClick={() => set(g, value[g].filter((_, j) => j !== i))}><X size={14} /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<Plus size={13} />} onClick={() => set(g, [...(value[g] || []), { field: "type", op: "eq", value: "" }])}>Add</Button>
        </Box>
      ))}
    </Box>
  );
};

// action type -> params editor
export const ACTIONS = {
  assign: { label: "Assign issue", params: [["to", "select", ["reporter", "project_lead", "component_lead", "user"]], ["memberId", "person"]] },
  set_priority: { label: "Set priority", params: [["priorityId", "priority"]] },
  set_field: { label: "Set field", params: [["field", "text", "e.g. storyPoints or cf.severity"], ["value", "text"]] },
  add_label: { label: "Add label", params: [["label", "text"]] },
  remove_label: { label: "Remove label", params: [["label", "text"]] },
  transition: { label: "Move to status", params: [["statusId", "status"], ["resolution", "resolution"]] },
  add_comment: { label: "Add comment", params: [["body", "multiline", "Supports {{issue_key}}, {{issue_summary}}"], ["internal", "bool"]] },
  create_subtask: { label: "Create sub-task", params: [["summary", "text"], ["assignTo", "select", ["", "assignee"]]] },
  notify: { label: "Send notification", params: [["to", "select", ["assignee", "reporter", "watchers", "project_lead", "project_manager", "role", "user"]], ["roleId", "role"], ["memberId", "person"], ["title", "text"], ["message", "multiline"]] },
  send_email: { label: "Send email", params: [["to", "select", ["assignee", "reporter", "watchers", "project_lead", "project_manager", "role", "user", "address"]], ["address", "text"], ["roleId", "role"], ["memberId", "person"], ["subject", "text"], ["body", "multiline"]] },
  webhook: { label: "Call webhook", params: [["url", "text", "https://…"], ["secret", "text", "HMAC secret (optional)"]] },
  add_watcher: { label: "Add watcher", params: [["memberId", "person"]] },
  create_activity: { label: "Write activity note", params: [["text", "text"]] },
  project_summary: { label: "Send project summary", params: [["to", "select", ["project_manager", "project_lead", "role", "user"]], ["roleId", "role"], ["memberId", "person"]] },
};

const ParamInput = ({ name, kind, extra, value, onChange }) => {
  const ctx = usePm();
  const common = { size: "small", label: name, value: value ?? "", sx: { ...fieldSx, minWidth: 160 } };
  const options = {
    select: (extra || []).map((v) => [v, v || "—"]),
    person: ctx.people.map((p) => [String(p.id), p.name]),
    priority: ctx.priorities.map((p) => [p._id, p.name]),
    status: ctx.statuses.map((s) => [s._id, s.name]),
    role: ctx.projectRoles.map((r) => [r._id, r.name]),
    resolution: ["", ...ctx.settings.resolutions].map((r) => [r, r || "—"]),
    bool: [["false", "No"], ["true", "Yes"]],
  }[kind];
  if (options) {
    return (
      <TextField select {...common} value={kind === "bool" ? String(Boolean(value)) : value ?? ""} onChange={(e) => onChange(kind === "bool" ? e.target.value === "true" : e.target.value)}>
        {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>
    );
  }
  return <TextField {...common} multiline={kind === "multiline"} minRows={kind === "multiline" ? 2 : undefined} placeholder={typeof extra === "string" ? extra : ""} onChange={(e) => onChange(e.target.value)} sx={{ ...fieldSx, flexGrow: 1, minWidth: 200 }} />;
};

export const ActionRows = ({ value = [], onChange, exclude = [] }) => (
  <Box>
    {value.map((a, i) => {
      const def = ACTIONS[a.type];
      return (
        <Box key={i} sx={{ p: 1, mb: 1, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField select size="small" value={a.type} onChange={(e) => onChange(value.map((x, j) => (j === i ? { type: e.target.value, params: {} } : x)))} sx={{ ...fieldSx, minWidth: 190 }}>
              {Object.entries(ACTIONS).filter(([k]) => !exclude.includes(k)).map(([k, d]) => <MenuItem key={k} value={k}>{d.label}</MenuItem>)}
            </TextField>
            {(def?.params || []).map(([name, kind, extra]) => (
              <ParamInput key={name} name={name} kind={kind} extra={extra} value={a.params?.[name]}
                onChange={(v) => onChange(value.map((x, j) => (j === i ? { ...x, params: { ...x.params, [name]: v } } : x)))} />
            ))}
            <IconButton size="small" onClick={() => onChange(value.filter((_, j) => j !== i))}><X size={14} /></IconButton>
          </Stack>
        </Box>
      );
    })}
    <Button size="small" startIcon={<Plus size={13} />} onClick={() => onChange([...value, { type: "add_label", params: {} }])}>Add action</Button>
    {!value.length && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>No actions.</Typography>}
  </Box>
);
